import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiPlay, FiSquare } from 'react-icons/fi';
import analytics from './analytics.js';
import MeasurementLegend from './MeasurementLegend.js';
import Footer from './Footer.js';
import { requestWakeLock, releaseWakeLock } from './wakeLock.js';
import { PiSpeakerHighLight } from "react-icons/pi";
import { PiSpeakerSimpleHighDuotone } from "react-icons/pi";
import { PiSpeakerSimpleSlash } from "react-icons/pi";
import { BiZoomIn, BiZoomOut, BiArrowBack, BiPlus, BiMinus, BiChevronDown, BiChevronUp } from "react-icons/bi";
import pieImage from './images/food/pie.png';
import { useFloating, autoUpdate, offset, shift, arrow, useClick, useDismiss, useRole, useInteractions } from '@floating-ui/react';
import { RecipeDetailSkeleton } from './SkeletonLoaders.js';

const AUDIO_STATE_KEY = 'audioState';


// Glowing fire effect under the pie
const FireGlowEffect = () => {
  return (
    <div className="fire-glow-effect">
      <div className="fire-base"></div>
      <div className="fire-glow"></div>
      <div className="fire-inner-glow"></div>
    </div>
  );
};

// Original Pie Image Component
const PieIcon = ({ size = 80, isCompleted = false, pieNumber }) => (
  <div className="pie-icon-container">
    <div className="pie-with-number">
      <img 
        src={pieImage} 
        alt={`Pie ${pieNumber}`}
        className={`original-pie-image ${isCompleted ? 'completed-pie lit-up' : 'pending-pie'}`}
        style={{ width: size, height: size }}
      />
      <div className="pie-number">{pieNumber}</div>
      {isCompleted && <FireGlowEffect />}
    </div>
  </div>
);


// Sets of IDs for specific audio files
const oneCrustPieIDs = new Set([625, 804, 682, 685, 692, 704]);
const twoCrustPieIDs = new Set([
  771, 780, 784, 788, 792, 796, 800, 590, 595, 600, 605, 610, 615,
  619, 629, 634, 652, 670, 674, 716, 728, 736, 741, 745,
]);

const measurementColors = {
  '⅛ tsp': '#ffffff',
  '1/8 tsp': '#ffffff',
  '⅛ teaspoon': '#ffffff',
  '¼ tsp': '#73a14a',
  '1/4 tsp': '#73a14a',
  '¼ teaspoon': '#73a14a',
  '½ tsp': '#3a68a8',
  '1/2 tsp': '#3a68a8',
  '½ teaspoons': '#3a68a8',
  '½ teaspoon': '#3a68a8',
  '1 tsp': '#e6341d',
  '1 tablespoon': '#6e386a',
  '1 Tablespoon': '#6e386a',
  '1 Tbsp': '#6e386a',
  '1 T': '#6e386a',
  '⅛ cup': '#ffffff',
  '1/8 cup': '#ffffff',
  '¼ cup': '#73a14a',
  '¼ cup,': '#73a14a',
  '1/4 cup': '#73a14a',
  '1/4 cup,': '#73a14a',
  '¼ cups': '#73a14a',
  '1/4 cups': '#73a14a',
  '1/4 cups,': '#73a14a',
  '⅓ cup': '#ffff46',
  '1/3 cup': '#ffff46',
  '½ cup': '#3a68a8',
  '½ cup, ': '#3a68a8',
  '½ cups': '#3a68a8',
  '½ cups,': '#3a68a8',
  '1/2 cup': '#3a68a8',
  '1/2 cups': '#3a68a8',
  '1/2 cups,': '#3a68a8',
  '⅔ cup': '#e96a25',
  '1 cup': '#e6341d',
  '3 oz': '#e96a25',
};

const isDarkColor = (color) => {
  const hexColor = color.replace('#', '');
  const r = parseInt(hexColor.substr(0, 2), 16);
  const g = parseInt(hexColor.substr(2, 2), 16);
  const b = parseInt(hexColor.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness < 128;
};

const API_URL = process.env.REACT_APP_API_URL; 
// enable when you want to use mobile locally. const API_URL = "http://192.168.1.39:3003";

function RecipeDetail() {
  const [recipe, setRecipe] = useState(null);
  const [ingredients, setIngredients] = useState({});
  const [directions, setDirections] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeStep, setActiveStep] = useState(null);
  const [textSize, setTextSize] = useState(18);
  const [checkedIngredients, setCheckedIngredients] = useState({});
  const [isLegendVisible, setIsLegendVisible] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [audioState, setAudioState] = useState(() => {
    return localStorage.getItem(AUDIO_STATE_KEY) || 'on';
  });
  const [volume] = useState(1); // Volume state, 1 is the max volume
  const [pieCount, setPieCount] = useState(0);
  const [completedPies, setCompletedPies] = useState(new Set());
  const [isPieTrackerExpanded, setIsPieTrackerExpanded] = useState(false);
  const [isLoadingRecipe, setIsLoadingRecipe] = useState(true);
  const [isLoadingIngredients, setIsLoadingIngredients] = useState(true);
  const [isLoadingDirections, setIsLoadingDirections] = useState(true);
  // const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const { id } = useParams();

  // Floating UI setup for pie tracker popover
  const arrowRef = useRef(null);
  const { refs, floatingStyles, context } = useFloating({
    open: isPieTrackerExpanded,
    onOpenChange: setIsPieTrackerExpanded,
    middleware: [
      offset(15),
      shift({ padding: 15 }),
      arrow({ element: arrowRef })
    ],
    whileElementsMounted: autoUpdate,
    placement: 'bottom',
    strategy: 'absolute'
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    requestWakeLock();
    
    return () => {
      releaseWakeLock();
    };
  }, []);

  useEffect(() => {
    // Scroll to top when recipe changes
    window.scrollTo(0, 0);
    
    setIsLoadingRecipe(true);
    fetch(`${API_URL}/api/recipe_name_description/${id}`)
      .then((response) => response.json())
      .then((data) => {
        setRecipe(data.data);
        setIsLoadingRecipe(false);
        
        // Track recipe view
        if (data.data) {
          analytics.trackRecipeView(parseInt(id), data.data.recipe_name);
          analytics.trackWakeLockRequest(parseInt(id));
        }
      })
      .catch(() => {
        setError('Failed to fetch recipe details');
        setIsLoadingRecipe(false);
      });
  }, [id]);

  useEffect(() => {
    setIsLoadingDirections(true);
    fetch(`${API_URL}/api/directions/${id}`)
      .then((response) => response.json())
      .then((data) => {
        setDirections(data.data);
        setIsLoadingDirections(false);
      })
      .catch(() => {
        setError('Failed to fetch directions');
        setIsLoadingDirections(false);
      });
  }, [id]);

  useEffect(() => {
    setIsLoadingIngredients(true);
    fetch(`${API_URL}/api/ingredients/${id}`)
      .then((response) => response.json())
      .then((data) => {
        const sectionedIngredients = data.data.reduce((acc, ingredient) => {
          acc[ingredient.section] = acc[ingredient.section] || [];
          acc[ingredient.section].push(ingredient);
          return acc;
        }, {});
        setIngredients(sectionedIngredients);
        const initialExpandedState = {};
        Object.keys(sectionedIngredients).forEach((section) => {
          initialExpandedState[section] = Object.keys(sectionedIngredients).length === 1;
        });
        setExpandedSections(initialExpandedState);

        const initialCheckedState = {};
        data.data.forEach((ingredient) => {
          initialCheckedState[ingredient.id] = false;
        });
        setCheckedIngredients(initialCheckedState);
        setIsLoadingIngredients(false);
      })
      .catch(() => {
        setError('Failed to fetch ingredients');
        setIsLoadingIngredients(false);
      });
  }, [id]);

  // Save audioState to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(AUDIO_STATE_KEY, audioState);
  }, [audioState]);

  // Update the volume on the current audio element if present
  useEffect(() => {
    if (currentAudio) {
      currentAudio.audio.volume = volume;
    }
  }, [volume, currentAudio]);

  // Toggle audio state among 'on' -> 'faster' -> 'off'
  const toggleAudioState = () => {
    setAudioState((prevState) => {
      let newState;
      if (prevState === 'on') newState = 'faster';
      else if (prevState === 'faster') newState = 'off';
      else newState = 'on';
      
      // Track speed change
      if (currentAudio) {
        const oldRate = prevState === 'on' ? 0.87 : prevState === 'faster' ? 1.0 : 0;
        const newRate = newState === 'on' ? 0.87 : newState === 'faster' ? 1.0 : 0;
        analytics.trackAudioSpeedChange(id, currentAudio.stepNumber, oldRate, newRate);
      }
      
      return newState;
    });
  };
  const toggleAudioPlayback = (stepNumber, directionId) => {
    if (audioState === 'off') return;
  
    let audioPath;
    if (oneCrustPieIDs.has(directionId)) {
      audioPath = '/step-audio/one-crust-pie.mp3';
    } else if (twoCrustPieIDs.has(directionId)) {
      audioPath = '/step-audio/two-crust-pie.mp3';
    } else {
      audioPath = `/step-audio/recipe-${id}-step-${stepNumber}.mp3`;
    }
  
    if (currentAudio && currentAudio.stepNumber === stepNumber) {
      // Pausing current audio
      analytics.trackAudioPause(id, stepNumber, audioPath, currentAudio.audio.currentTime);
      currentAudio.audio.pause();
      currentAudio.audio.currentTime = 0;
      setCurrentAudio(null);
    } else {
      if (currentAudio) {
        // Stop previous audio
        analytics.trackAudioPause(id, currentAudio.stepNumber, currentAudio.audioPath, currentAudio.audio.currentTime);
        currentAudio.audio.pause();
        currentAudio.audio.currentTime = 0;
      }
      const newAudio = new Audio(audioPath);
  
      // Set inline controls for manual adjustment on iOS
      newAudio.controls = true;
      const playbackRate = audioState === 'on' ? 0.87 : audioState === 'slow' ? 0.87 : 1.00;
      newAudio.playbackRate = playbackRate;
  
      // Track audio play
      analytics.trackAudioPlay(id, stepNumber, audioPath, playbackRate);
  
      newAudio.addEventListener('ended', () => {
        analytics.trackAudioComplete(id, stepNumber, audioPath, newAudio.duration);
        setCurrentAudio(null);
      });
      
      newAudio.addEventListener('pause', () => {
        if (currentAudio && currentAudio.stepNumber === stepNumber) {
          analytics.trackAudioPause(id, stepNumber, audioPath, newAudio.currentTime);
        }
      });

      newAudio.play().catch((error) => {
        console.error("Audio playback failed:", error);
        analytics.trackError(error, { context: 'audio_playback', recipeId: id, stepNumber, audioPath });
      });
  
      setCurrentAudio({ audio: newAudio, stepNumber, audioPath });
    }
  };
  
  

  // const handleVolumeChange = (e) => {
  //   const newVolume = parseFloat(e.target.value);
  //   setVolume(newVolume);
  //   if (currentAudio) {
  //     currentAudio.audio.volume = newVolume;
  //   }
  // };

  const getSpeechButtonText = () => {
    if (audioState === 'on') {
      return (
        <>
          <PiSpeakerHighLight /> On
        </>
      );
    }
    if (audioState === 'faster') {
      return (
        <>
          <PiSpeakerSimpleHighDuotone /> Faster
        </>
      );
    }
    return (
      <>
        <PiSpeakerSimpleSlash /> Off
      </>
    );
  };

  const handleStepClick = (stepNumber) => {
    setActiveStep(prevStep => {
      const newStep = prevStep === stepNumber ? null : stepNumber;
      // Track step navigation
      analytics.trackStepNavigation(id, prevStep, newStep);
      return newStep;
    });
  };

  const handleButtonClick = (section) => {
    setExpandedSections((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }));
  };

  const toggleLegendVisibility = () => setIsLegendVisible(!isLegendVisible);

  const increaseTextSize = () => setTextSize((size) => size + 2);
  const decreaseTextSize = () => setTextSize((size) => size - 2);

  const handleCheckboxChange = (ingredientId) => {
    setCheckedIngredients((prevState) => {
      const newState = {
        ...prevState,
        [ingredientId]: !prevState[ingredientId]
      };
      
      // Track ingredient interaction (find ingredient step number)
      Object.entries(ingredients).forEach(([section, items]) => {
        const ingredient = items.find(item => item.id === ingredientId);
        if (ingredient) {
          analytics.trackIngredientHighlight(id, ingredient.step_number, ingredientId);
        }
      });
      
      return newState;
    });
  };

  const highlightMeasurements = (quantity) => {
    const parts = quantity
      .split(/(\s+\+\s+|\s+or\s+|\s*,\s*|\sto\s|\s*(?=\())/)
      .filter(Boolean);
    return parts.map((part, index) => {
      const trimmedPart = part.trim();
      const color = measurementColors[trimmedPart] || 'inherit';
      const textColor = isDarkColor(color) ? '#ffffff' : '#000000';
      const hasBorder = color !== 'inherit';
      return (
        <span
          key={index}
          style={{
            backgroundColor: color,
            color: textColor,
            padding: '4px 4px',
            borderRadius: '4px',
            marginBottom: '20px',
            border: hasBorder ? '1px solid #000000' : 'none',
          }}
        >
          {trimmedPart}
        </span>
      );
    });
  };

  // Pie counter functions
  const isPieRecipe = () => {
    return recipe && recipe.recipe_name && recipe.recipe_name.toLowerCase().includes('pie');
  };

  const addPie = () => {
    setPieCount(prev => prev + 1);
  };

  const removePie = () => {
    if (pieCount > 0) {
      setPieCount(prev => {
        const newCount = prev - 1;
        // Remove completed status for pies beyond the new count
        setCompletedPies(prevCompleted => {
          const newCompleted = new Set();
          prevCompleted.forEach(pieIndex => {
            if (pieIndex < newCount) {
              newCompleted.add(pieIndex);
            }
          });
          return newCompleted;
        });
        return newCount;
      });
    }
  };

  const togglePieComplete = (pieIndex) => {
    setCompletedPies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pieIndex)) {
        newSet.delete(pieIndex);
      } else {
        newSet.add(pieIndex);
      }
      return newSet;
    });
  };

  const togglePieTracker = () => {
    setIsPieTrackerExpanded(prev => !prev);
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  // Show skeleton loading while any critical data is still loading
  const isLoading = isLoadingRecipe || isLoadingDirections || isLoadingIngredients;
  const hasData = recipe && directions && Object.keys(ingredients).length > 0;
  
  if (isLoading || !hasData) {
    return (
      <>
        <RecipeDetailSkeleton />
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="controls">
        <div className="header-controls">
          <Link className="back-button" to="/">
            <BiArrowBack size={20} />
            <span>Back to Recipes</span>
          </Link>
          <div className="right-side-controls">
            <div className="accessibility-buttons-right">
              <button className="accessibility-btn audio-btn" onClick={toggleAudioState}> 
                {getSpeechButtonText()}
                <br />
                <span style={{color:'#6B7280', fontSize:'12px'}}>Tap to change</span>
              </button>
              <button className="accessibility-btn font-btn" onClick={increaseTextSize}>
                <BiZoomIn size={20} /> Larger Font
                <br />
                <span style={{color:'#6B7280', fontSize:'12px'}}>Tap to change</span>
              </button>
              <button className="accessibility-btn font-btn" onClick={decreaseTextSize}>
                <BiZoomOut size={20} /> Smaller Font
                <br />
                <span style={{color:'#6B7280', fontSize:'12px'}}>Tap to change</span>
              </button>
            </div>
            
            {/* Pie Counter - Only show for pie recipes */}
            {isPieRecipe() && (
              <>
                <div 
                  ref={refs.setReference}
                  className="pie-tracker-compact"
                  {...getReferenceProps()}
                >
                  <div className="pie-tracker-summary">
                    <div className="pie-status-summary">
                      <h3 style={{ fontSize: `${textSize + 2}px` }}>Pie Progress</h3>
                      <span className="pie-progress-text" style={{ fontSize: `${textSize}px` }}>
                        {pieCount === 0 
                          ? 'Tap here to track pies' 
                          : `${completedPies.size} of ${pieCount} done`
                        }
                      </span>
                    </div>
                    <button className="pie-tracker-toggle">
                      {isPieTrackerExpanded ? <BiChevronUp size={Math.max(16, textSize + 4)} /> : <BiChevronDown size={Math.max(16, textSize + 4)} />}
                    </button>
                  </div>
                </div>

                {/* Floating UI Pie Tracker Popover */}
                <div
                  ref={refs.setFloating}
                  style={floatingStyles}
                  className="pie-tracker-popover"
                  data-show={isPieTrackerExpanded ? '' : undefined}
                  {...getFloatingProps()}
                >
                    <div ref={arrowRef} className="pie-tracker-arrow" />
                    <div className="pie-tracker-content">
                      <div className="pie-counter-controls">
                        <button className="pie-control-btn" onClick={removePie} disabled={pieCount <= 0}>
                          <BiMinus size={20} />
                        </button>
                        <span className="pie-count-display" style={{ fontSize: `${textSize + 2}px` }}>
                          {pieCount === 0 ? 'Add pies to track' : `Making ${pieCount} pie${pieCount > 1 ? 's' : ''}`}
                        </span>
                        <button className="pie-control-btn" onClick={addPie}>
                          <BiPlus size={20} />
                        </button>
                      </div>
                      <div className="pie-grid-popover">
                        {Array.from({ length: pieCount }, (_, index) => (
                          <div
                            key={index}
                            className={`pie-item-popover ${completedPies.has(index) ? 'completed' : 'pending'}`}
                            onClick={() => togglePieComplete(index)}
                          >
                            <div className="pie-icon-wrapper">
                              <PieIcon 
                                size={70} 
                                isCompleted={completedPies.has(index)}
                                pieNumber={index + 1}
                              />
                            </div>
                            <div className={`pie-status ${completedPies.has(index) ? 'done' : 'todo'}`} style={{ fontSize: `${textSize - 2}px` }}>
                              {completedPies.has(index) ? 'Done' : `Pie ${index + 1}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="container">
        <div className="header-with-button">
          <h1 className="recipe-title" style={{ fontSize: `24px` }}>{recipe.recipe_name}</h1>
          <button className="ML_button" onClick={toggleLegendVisibility}>
            {isLegendVisible ? 'Hide Measure Guide' : 'Show Measure Guide'}
          </button>
        </div>
        {isLegendVisible && (
          <div className="fixed-legend">
            <MeasurementLegend />
          </div>
        )}

        <div className="content">
          <div className={`ingredients-section ${
            (Object.keys(ingredients).length === 1 && Object.keys(ingredients)[0] === 'Ingredients') ? 'hide-header' : ''
          } ${activeStep ? 'has-active-step' : ''}`}>
            <h3>Ingredients</h3>
            {Object.entries(ingredients).map(([section, items], index) => (
              <div
                className={`section ${
                  expandedSections[section] ? 'expanded' : 'collapsed'
                }`}
                key={index}
              >
                <div className="section-header">
                  <h4>{section}</h4>
                  <button
                    className="button"
                    onClick={() => handleButtonClick(section)}
                  >
                    {expandedSections[section]
                      ? 'Tap to collapse'
                      : 'Tap to expand'}
                  </button>
                </div>
                
                {expandedSections[section] && (
                  <>
                    {/* Desktop table layout */}
                    <table>
                      <thead>
                        <tr>
                          <th style={{ fontSize: `${textSize}px` }}></th>
                          <th style={{ fontSize: `${textSize}px` }}>
                            Ingredient
                          </th>
                          <th style={{ fontSize: `${textSize}px` }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((ingredient, idx) => (
                          <tr
                            key={ingredient.id}
                            style={{
                              backgroundColor:
                                ingredient.step_number === activeStep
                                  ? '#def'
                                  : 'transparent',
                            }}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={checkedIngredients[ingredient.id]}
                                onChange={() =>
                                  handleCheckboxChange(ingredient.id)
                                }
                              />
                            </td>
                            <td
                              data-step={ingredient.step_number}
                              style={{
                                fontSize: `${textSize}px`,
                                backgroundColor:
                                  activeStep === ingredient.step_number
                                    ? '#def'
                                    : 'transparent',
                              }}
                              className={checkedIngredients[ingredient.id] ? 'marker-strikethrough' : ''}
                            >
                              {ingredient.ingredient}
                            </td>
                            <td
                              data-step={ingredient.step_number}
                              style={{
                                fontSize: `${textSize - 3}px`,
                                backgroundColor:
                                  activeStep === ingredient.step_number
                                    ? '#def'
                                    : 'transparent',
                                whiteSpace: 'nowrap',
                              }}
                              className={checkedIngredients[ingredient.id] ? 'marker-strikethrough' : ''}
                            >
                              {highlightMeasurements(ingredient.quantity)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Mobile card layout */}
                    <div className="mobile-ingredients">
                      {items.map((ingredient, idx) => (
                        <div
                          key={ingredient.id}
                          className={`mobile-ingredient-card ${checkedIngredients[ingredient.id] ? 'checked' : ''}`}
                          style={{
                            backgroundColor:
                              ingredient.step_number === activeStep
                                ? '#e3f2fd'
                                : 'white',
                          }}
                        >
                          <div className="mobile-checkbox-row">
                            <input
                              type="checkbox"
                              checked={checkedIngredients[ingredient.id]}
                              onChange={() =>
                                handleCheckboxChange(ingredient.id)
                              }
                            />
                            <span className="mobile-checkbox-label">
                              {checkedIngredients[ingredient.id] ? 'Added' : 'Add to recipe'}
                            </span>
                          </div>
                          <div 
                            className="mobile-ingredient-name"
                            style={{ fontSize: `${textSize}px` }}
                          >
                            {ingredient.ingredient}
                          </div>
                          <div 
                            className="mobile-ingredient-amount"
                            style={{ fontSize: `${textSize - 1}px` }}
                          >
                            {highlightMeasurements(ingredient.quantity)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="directions-section" style={{ fontSize: `${textSize}px` }}>
 {/*         <div className="directions-section" style={{ fontSize: `${textSize}px` }}>
  {audioState !== 'off' && (
    <div style={{ display: 'flex', alignItems: 'center', float: 'right' }}>
      <span style={{ paddingRight: '10px', color:'rgb(185 185 185)' }}>Adjust volume</span>
      
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={volume}
        onChange={handleVolumeChange}
        style={{ width: '100px' }}
        aria-label="Volume control"
      />
    </div>*/}

  <h3>Directions</h3>
  <ol>
    {directions.map((direction, index) => (
      <li
        key={index}
        onClick={() => handleStepClick(direction.step_number)}
        style={{
          width: '90%',
          borderRadius: '8px',
          padding: '10px',
          cursor: 'pointer',
          backgroundColor:
            activeStep === direction.step_number ? '#def' : 'transparent',
        }}
      >
        <div className="direction-content">
          <div className="step-number-with-audio">
            <span className="step-number">{direction.step_number}</span>
            {audioState !== 'off' && (
              <button
                className="audio-play-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleAudioPlayback(direction.step_number, direction.id);
                }}
              >
                {currentAudio && currentAudio.stepNumber === direction.step_number ? (
                  <FiSquare />
                ) : (
                  <FiPlay />
                )}
              </button>
            )}
          </div>
          <div className="direction-text">
            {direction.direction_text}
          </div>
        </div>
      </li>
    ))}
  </ol>
</div>

        </div>
      </div>
      <Footer />
    </>
  );
}

export default RecipeDetail;
